/**
 * RemoveLeagueModal
 *
 * Confirmation step for disconnecting a league from JAI.
 *
 * Removing is reversible — the league can be reconnected later from
 * "Add League" — so this asks once and gets out of the way rather than
 * making the user type the league name to confirm.
 *
 * Props:
 *   league    — the league being removed ({ id, name, ... })
 *   onClose   — close without removing
 *   onRemoved — removal succeeded; caller updates its own state / navigates
 */

import { useEffect, useState } from 'react'
import { leaguesApi } from '../services/api'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import { Notice } from './JaiV2'

export default function RemoveLeagueModal({ league, onClose, onRemoved }) {
  const [removing, setRemoving] = useState(false)
  const [error,    setError]    = useState('')

  // Escape closes the dialog — but not mid-request, so the user can't walk away
  // unsure whether the removal went through.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !removing) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [removing, onClose])

  const handleRemove = async () => {
    setRemoving(true)
    setError('')
    try {
      await leaguesApi.remove(league.id)
      onRemoved()
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove this league — please try again.')
      setRemoving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-50 px-4"
      onClick={() => { if (!removing) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-league-title"
        className="bg-white border rounded-2xl w-full max-w-md shadow-2xl"
        style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', borderColor: JAI_COLORS.hair }}
        onClick={e => e.stopPropagation()}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${JAI_COLORS.hair}` }}>
          <h2
            id="remove-league-title"
            style={{ fontFamily: JAI_FONTS.tight, fontSize: 17, fontWeight: 600, color: JAI_COLORS.navy }}
          >
            Are you sure you want to delete this league?
          </h2>
          <button
            onClick={onClose}
            disabled={removing}
            aria-label="Close"
            style={{ color: JAI_COLORS.mute, fontSize: 28, lineHeight: 1, transition: 'color 0.2s', cursor: removing ? 'not-allowed' : 'pointer', background: 'none', border: 'none' }}
            onMouseEnter={e => { if (!removing) e.target.style.color = JAI_COLORS.navy }}
            onMouseLeave={e => e.target.style.color = JAI_COLORS.mute}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5" style={{ fontFamily: JAI_FONTS.tight }}>
          <p style={{ fontSize: 14, color: JAI_COLORS.navy, lineHeight: 1.5, marginBottom: 10 }}>
            <strong style={{ fontWeight: 700 }}>{league.name}</strong> will be deleted from JAI. Your
            roster, tools, and recommendations for it will no longer appear.
          </p>
          <p style={{ fontSize: 13, color: JAI_COLORS.mute, lineHeight: 1.5 }}>
            Nothing changes in your {(league.platform || 'fantasy').toUpperCase()} account — this only deletes it
            from JAI, and you can reconnect it any time from <strong style={{ fontWeight: 600 }}>Add League</strong>.
          </p>

          {error && (
            <Notice tone="error" style={{ marginTop: 14 }}>{error}</Notice>
          )}

          {/* Actions */}
          <div className="flex gap-3" style={{ marginTop: 20 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={removing}
              className="flex-1"
              style={{ background: JAI_COLORS.paper, color: JAI_COLORS.navy, border: `1px solid ${JAI_COLORS.hair}`, fontWeight: 500, fontSize: 13, borderRadius: 8, padding: '10px 16px', cursor: removing ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontFamily: JAI_FONTS.tight }}
              onMouseEnter={e => { if (!removing) e.target.style.background = JAI_COLORS.hair }}
              onMouseLeave={e => { e.target.style.background = JAI_COLORS.paper }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={removing}
              className="flex-1"
              style={{ background: removing ? 'rgba(214,58,58,0.6)' : JAI_COLORS.error, color: 'white', fontWeight: 600, fontSize: 13, borderRadius: 8, padding: '10px 16px', border: 'none', cursor: removing ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontFamily: JAI_FONTS.tight, boxShadow: removing ? 'none' : '0 4px 16px rgba(214,58,58,0.25)' }}
              onMouseEnter={e => { if (!removing) { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 6px 20px rgba(214,58,58,0.32)' } }}
              onMouseLeave={e => { if (!removing) { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 16px rgba(214,58,58,0.25)' } }}
            >
              {removing ? 'Deleting…' : 'Yes, delete league'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
